import { toast } from '@/shared/lib/toast';
import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { payloadString, payloadNumber, payloadBool } from '../realtime-sync.payload';
import { setProgressAnchor } from '@/shared/lib/progress';
import {
  AnalyticsEvent,
  trackEventOnceForId,
} from '@/shared/lib/analytics';
import type { DataChangedEvent } from '../realtime-sync.types';
import { getAppRouter } from '@/shared/lib/app-router';

/**
 * Estimate realtime handler (entity `estimates`, actions created/updated/
 * deleted). The server recomputes estimate totals after every item mutation
 * and after generation finishes, then broadcasts `estimates/updated` — so this
 * is the reconcile point (L2 on WS reconnect; L1 is the query refetch on
 * mutation) that pulls authoritative totals and the latest status.
 *
 * Payload carries `concept_id`; `event.id` is the estimate id. We invalidate
 * the concept-scoped list (so a new/removed estimate appears) and the affected
 * estimate's detail (so the editor refetches items + totals).
 */
export async function handleEstimateChanged(
  event: DataChangedEvent
): Promise<void> {
  const conceptId = payloadString(event.payload, 'concept_id');

  wsLogger.debug('[RealtimeSync] Estimate changed:', {
    estimateId: event.id,
    action: event.action,
    conceptId,
  });

  if (conceptId) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.estimates.list(conceptId),
    });
    // The dedicated project Estimates page (FE3) aggregates across concepts and
    // isn't keyed by concept_id — sweep every project-scoped list so a new/
    // removed/updated estimate reflects there too. The prefix matches every
    // `estimates.projectList(projectId)` regardless of project.
    queryClient.invalidateQueries({
      queryKey: ['estimates', 'project-list'],
    });
  } else {
    // Fallback: sweep every estimate list when the broadcast omits concept_id.
    queryClient.invalidateQueries({ queryKey: cacheKeys.estimates.all });
  }

  if (event.id) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.estimates.detail(event.id),
    });
  }
}

/**
 * Estimate generation lifecycle (jobs pipeline — entity `estimates`, actions
 * queued/started/completed/failed). Mirrors the album lifecycle handlers: the
 * queue snapshot owns the job list, these handlers own progress anchors,
 * toasts, and cache invalidation.
 */
export async function handleEstimateStarted(
  event: DataChangedEvent
): Promise<void> {
  if (!event.payload || !event.id) return;

  // Progress anchor for smooth time-based progress before the snapshot lands.
  const durationPredicted = payloadNumber(event.payload, 'duration_predicted');
  if (durationPredicted && durationPredicted > 0) {
    setProgressAnchor(event.id, {
      anchorUptime: performance.now(),
      anchorProgress: 0,
      durationPredicted,
    });
  }

  await handleEstimateChanged(event);
  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
}

export async function handleEstimateCompleted(
  event: DataChangedEvent
): Promise<void> {
  const payload = event.payload;
  const conceptId = payloadString(payload, 'concept_id');
  const projectId = payloadString(payload, 'project_id');

  wsLogger.info('[RealtimeSync] Estimate completed:', {
    estimateId: event.id,
    conceptId,
  });

  // Funnel: estimate generation finished (pairs with estimate_requested). The
  // once-per-id guard absorbs WS redelivery/reconnect replays.
  if (event.id) {
    trackEventOnceForId(AnalyticsEvent.ESTIMATE_GENERATED, event.id);
  }

  // Success toast — body click navigates to the concept's Estimate tab.
  // API-driven messages: skip toast if payload omits localized title. `silent`
  // estimates are generated as part of an album (the album posts its own
  // "Blueprint ready" toast, which fires minutes later when the album actually
  // finishes — so the estimate toast would mislead); the server already drops
  // the toast copy for them, the flag check is the explicit belt-and-braces.
  // `n` is the arrival nonce (completed estimate id) so a repeat tap re-applies
  // the tab even when the user is already on the concept page.
  {
    const silent = payloadBool(payload, 'silent') === true;
    const title = payloadString(payload, 'title');
    if (title && !silent) {
      const description = payloadString(payload, 'message');
      const canNavigate = Boolean(projectId && conceptId);

      toast.completion(title, {
        ...(description && { description }),
        ...(canNavigate && {
          onTap: () => {
            getAppRouter()?.push(
              `/project/${projectId}/concept/${conceptId}?tab=estimate&n=${event.id}`
            );
          },
        }),
      });
    }
  }

  await handleEstimateChanged(event);
  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
}

export async function handleEstimateFailed(
  event: DataChangedEvent
): Promise<void> {
  const payload = event.payload;

  wsLogger.warn('[RealtimeSync] Estimate failed:', {
    estimateId: event.id,
    error: payload?.error,
  });

  // Error toast — API-driven only.
  {
    const title = payloadString(payload, 'title');
    if (title) {
      const description =
        payloadString(payload, 'message') ?? payloadString(payload, 'error');
      toast.error(title, {
        ...(description && { description }),
        duration: 8000,
      });
    }
  }

  await handleEstimateChanged(event);
  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
}
